/**
 * The Start band — the dashboard's primary launcher, under BOTH standards (founder decision D8).
 *
 * DocuSign's own shape: a `Start ▾` menu beside three named doors, on the accent, at the top of the
 * working surface. It replaces the four-card "Start something" grid, which had a defect worth
 * naming because it is the reason this exists: two of its four cards ("Upload a document" and
 * "Send for signature") pointed at the SAME route, so the product's primary call to action was two
 * cards for one door, and the wording of neither said which.
 *
 * ## What differs between the standards, and what does not
 *
 * Almost nothing, now. The two envelope doors open the SAME single-page composer under both
 * standards (ADR 0102) — the difference the founder walked back on 2026-09-15, after being shown
 * the classic flow beside the International one. Only "Use Envelope Template" still forks, and it
 * forks for a stated reason rather than a historical one. Both answers stay in one function
 * (`destinations`), so a reader sees them at once rather than hunting for a branch.
 *
 * ## Why the menu is hand-rolled
 *
 * `packages/ui`'s `Menu` is a flat list of items and this is a two-section menu with headings.
 * Extending `Menu` for one caller would put a feature in the shared component that every other
 * caller has to read past; this is thirty lines with the same keyboard contract (Escape closes,
 * focus returns to the trigger, `aria-expanded` on the trigger, `role="menu"` on the list).
 */
import { useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Icon } from '@flowza/ui'
import { useWorkflow } from '../../auth/workflow'

/**
 * What the sender is setting out to do. Carried into the composer as `?preset=`, where it decides
 * the initial shape — not a mode, and nothing the composer refuses to change afterwards.
 */
export type StartPreset = 'get_signatures' | 'sign_only' | 'from_template'

/**
 * Where each door leads, per standard.
 *
 * Both answers in one function on purpose. A `standard === 'international' ? … : …` at each of the
 * six call sites would be six places to get one rule wrong, and the reason the old launcher sent
 * two cards to one route is precisely that its destinations were written where they were used.
 */
export function destinations(standard: string): Record<StartPreset, string> {
  return standard === 'international'
    ? {
        get_signatures: '/app/envelopes/new?preset=get_signatures',
        sign_only: '/app/envelopes/new?preset=sign_only',
        from_template: '/app/envelopes/new?preset=from_template',
      }
    : {
        // The SAME set-up screen since ADR 0102. It was `/app/documents` — the envelopes list with
        // its subject-first card — and the founder's answer to being shown the two designs side by
        // side was that they should be one. The composer carries the standard's own subject rule
        // inside it, so nothing about `flowza_sign` is softened by arriving here.
        get_signatures: '/app/envelopes/new?preset=get_signatures',
        sign_only: '/app/envelopes/new?preset=sign_only',
        // Templates is the exception, and it is the honest one: the composer carries `preset` in
        // its URL and does nothing with it yet, so sending a classic sender there would trade a
        // working template list for a parameter nobody reads. It moves when the composer can start
        // from a template.
        from_template: '/app/templates',
      }
}

const BUTTONS: ReadonlyArray<{
  readonly preset: StartPreset
  readonly label: string
  readonly icon: 'send' | 'pen' | 'template'
}> = [
  { preset: 'get_signatures', label: 'Get Signatures', icon: 'send' },
  // `pen`, not `edit`: the icon set has no `edit`, and a pen is what the screenshot shows and what
  // "sign this yourself" means to a reader anyway.
  { preset: 'sign_only', label: 'Sign Document', icon: 'pen' },
  { preset: 'from_template', label: 'Use Envelope Template', icon: 'template' },
]

export function StartBand() {
  const { profile } = useWorkflow()
  const to = destinations(profile.workflow_standard)

  return (
    <section
      aria-labelledby="start-band"
      // The accent gradient, in TOKENS only — `bg-accent` to `bg-accent-hover`, both of which a
      // tenant's branding repaints. A literal gradient would be the one band on the console that
      // ignored a tenant's colours, and `pnpm lint:design-tokens` would refuse it anyway.
      className="from-accent to-accent-hover text-text-on-accent rounded-xl bg-gradient-to-r p-4 sm:p-5"
    >
      <h2 id="start-band" className="sr-only">
        Start something
      </h2>
      <div className="flex flex-wrap items-center gap-2">
        <StartMenu to={to} />
        {BUTTONS.map((button) => (
          <Link
            key={button.preset}
            to={to[button.preset]}
            // `bg-text-on-accent/10` would be a token with an opacity modifier, which the token lint
            // accepts; a border plus a transparent ground reads better on every brand colour than a
            // tinted fill does, because a tint of an unknown accent can land anywhere.
            className="border-text-on-accent/40 text-text-on-accent hover:bg-text-on-accent/10 focus-visible:outline-focus-ring inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium focus-visible:outline-2"
          >
            <Icon name={button.icon} size={16} />
            {button.label}
          </Link>
        ))}
      </div>
    </section>
  )
}

/** `Start ▾` — two sections with headings, which is why it is not `packages/ui`'s flat Menu. */
function StartMenu({ to }: { readonly to: Record<StartPreset, string> }) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const menuId = useId()
  const navigate = useNavigate()

  // Escape closes and focus goes BACK to the trigger. The obligation nobody notices is missing: a
  // menu that closes and leaves focus on the body strands a keyboard user at the top of the page.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) === true) return
      if (triggerRef.current?.contains(target) === true) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  const go = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        className="bg-text-on-accent text-accent focus-visible:outline-focus-ring inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold focus-visible:outline-2"
      >
        Start
        <Icon name="chevron-down" size={16} />
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Start"
          className="border-border-strong bg-surface-raised shadow-overlay absolute left-0 z-40 mt-2 w-72 rounded-md border p-2"
        >
          <MenuSection title="Agreements" caption="Envelopes">
            <MenuItem label="Get Signatures" onSelect={() => go(to.get_signatures)} />
            <MenuItem label="Sign a Document" onSelect={() => go(to.sign_only)} />
            <MenuItem label="Use a Template" onSelect={() => go(to.from_template)} />
          </MenuSection>
          <MenuSection title="Templates" caption="Envelope Templates">
            {/*
              Both go to the templates screen, and they are two items rather than one because they
              are two intentions — and the screen answers both. Two items pointing at one route is
              what the old launcher did wrong; the difference is that these two SAY they are
              different things to do in the same place, where those two claimed to be different
              places.
            */}
            {/*
              A template is made FROM an envelope — there is no second editing surface (ux.md §6.2)
              — so "Create" opens the library with a one-line callout saying which envelope's ⋯
              menu to use, rather than a Templates screen that could only say the same thing.
            */}
            <MenuItem label="Create a Template" onSelect={() => go('/app/library?create=1')} />
            <MenuItem label="Browse Templates" onSelect={() => go('/app/templates')} />
          </MenuSection>
        </div>
      ) : null}
    </div>
  )
}

function MenuSection({
  title,
  caption,
  children,
}: {
  readonly title: string
  readonly caption: string
  readonly children: React.ReactNode
}) {
  return (
    <div className="not-last:border-border-subtle not-last:mb-2 not-last:border-b not-last:pb-2">
      <p className="text-text-muted m-0 px-2 pb-1 text-xs font-semibold uppercase tracking-wide">
        {title}
      </p>
      <p className="text-text-muted m-0 px-2 pb-1 text-xs">{caption}</p>
      {children}
    </div>
  )
}

function MenuItem({ label, onSelect }: { readonly label: string; readonly onSelect: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className="text-text-primary hover:bg-surface-sunken focus-visible:outline-focus-ring block w-full rounded-sm px-2 py-1.5 text-left text-sm focus-visible:outline-2"
    >
      {label}
    </button>
  )
}

/**
 * The first-run panel beneath the band: an illustration, a sentence, and one door.
 *
 * The illustration is an inline SVG in tokens rather than an asset, because an asset is a second
 * request on the one screen whose whole job is to be fast and welcoming, and because a raster
 * illustration cannot follow a tenant's accent.
 */
export function StartEmptyState() {
  const { profile } = useWorkflow()
  const to = destinations(profile.workflow_standard)
  return (
    <div className="border-card-border bg-card-bg rounded-xl border p-8 text-center">
      <svg
        viewBox="0 0 120 90"
        role="img"
        aria-label="A document with a signature line"
        className="mx-auto mb-4 h-24 w-32"
      >
        <rect
          x="22"
          y="8"
          width="60"
          height="74"
          rx="4"
          className="fill-surface-sunken stroke-border-strong"
          strokeWidth="2"
        />
        <line x1="32" y1="24" x2="72" y2="24" className="stroke-border-strong" strokeWidth="2" />
        <line x1="32" y1="34" x2="64" y2="34" className="stroke-border-strong" strokeWidth="2" />
        <line x1="32" y1="44" x2="70" y2="44" className="stroke-border-strong" strokeWidth="2" />
        <path
          d="M34 66 c6 -10 12 8 18 -2 c5 -8 10 6 16 -4"
          className="stroke-accent"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
        <line x1="32" y1="72" x2="72" y2="72" className="stroke-border-strong" strokeWidth="2" />
      </svg>
      {/*
        "Nothing here yet" and NOT "Send your first document for signature", which is
        `FirstEnvelope`'s heading on the first-run screen. Two panels under one heading on two
        screens is how a reader stops trusting either — and the distinction is real: this is an
        empty LIST, which an organization with nine completed envelopes and a filter can also be in,
        where the first-run card is "you have never sent anything".
      */}
      <h3 className="text-text-primary m-0 text-lg font-semibold">Nothing here yet</h3>
      <p className="text-text-muted mx-auto mt-2 max-w-md text-sm">
        Envelopes appear here as they move — sent, viewed, signed, completed. Upload a PDF or an
        Office file, say who needs to sign it, and place the fields. Everyone you send to gets their
        own link; they do not need a FlowZa account.
      </p>
      <Link
        to={to.get_signatures}
        className="bg-accent text-text-on-accent hover:bg-accent-hover focus-visible:outline-focus-ring mt-5 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold focus-visible:outline-2"
      >
        <Icon name="send" size={16} />
        Get Signatures
      </Link>
    </div>
  )
}
